import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";
import {
  DayPicker,
  DateRange,
  getDefaultClassNames,
  ClassNames,
} from "react-day-picker";
import "react-day-picker/style.css";
import { createElement, CSSProperties } from "react";
import { format } from "date-fns";
import * as Popover from "@radix-ui/react-popover";
import { Calendar } from "react-feather";
import cn from "../../../../../utils/cn";
import "./survey-question-calendar.css";

const CALENDAR_QUESTION_TYPE = "calendar";
export class SurveyQuestionCalendar extends SurveyQuestionElementBase {
  state = {
    formattedDate: "",
    selectedDate: undefined as Date | undefined,
    selectedRange: undefined as DateRange | undefined,
  };

  private lastDisableBeforeTime: number | null = null;

  componentDidMount() {
    this.lastDisableBeforeTime = this.disableBefore?.getTime() || null;
    switch (this.mode) {
      case "single":
        this.setState({ selectedDate: this.question.value });
        if (this.question.value) {
          this.setState({
            formattedDate: format(this.question.value, "dd/MM/yyyy"),
          });
        }
        break;
      case "range":
        this.setState({ selectedRange: this.question.value });
        if (this.question.value) {
          this.setState({
            formattedDate:
              format(this.question.value.from, "dd/MM/yyyy") +
              " - " +
              format(this.question.value.to, "MM/dd/yyyy"),
          });
        }
    }
  }

  componentDidUpdate() {
    const currentDisableBeforeTime = this.disableBefore?.getTime() || null;
    if (this.lastDisableBeforeTime !== currentDisableBeforeTime) {
      this.lastDisableBeforeTime = currentDisableBeforeTime;
      this.validateAndResetValue();
    }
  }

  validateAndResetValue() {
    const disableBefore = this.question.disableBefore;
    if (!disableBefore) return;

    if (this.mode === "single" && this.state.selectedDate) {
      if (this.state.selectedDate < disableBefore) {
        this.handleDayPickerSelect(undefined);
      }
    } else if (this.mode === "range" && this.state.selectedRange) {
      const { from, to } = this.state.selectedRange;
      if ((from && from < disableBefore) || (to && to < disableBefore)) {
        this.handleRangePickerSelect(undefined);
      }
    }
  }

  handleDayPickerSelect = (date: Date | undefined) => {
    if (!date) {
      this.setState({ formattedDate: "", selectedDate: undefined });
      this.question.value = "";
    } else {
      const formattedDate = format(date, "dd/MM/yyyy");
      this.setState({
        selectedDate: date,
        formattedDate: formattedDate,
      });
      this.question.value = date;
    }

    this.setState({ isPopoverOpen: false });
  };

  handleRangePickerSelect = (range: DateRange | undefined) => {
    if (!range || !range.from || !range.to) {
      this.setState({ selectedRange: undefined });
      this.question.value = "";
    } else {
      this.setState({
        formattedDate:
          format(range.from, "dd/MM/yyyy") +
          " - " +
          format(range.to, "dd/MM/yyyy"),
      });
      this.setState({ selectedRange: range });
      this.question.value = range;
    }
  };

  get mode() {
    return this.question.mode;
  }

  get numberOfMonths() {
    return this.question.numberOfMonths;
  }

  get question() {
    return this.questionBase;
  }

  get disableBefore() {
    if (typeof this.question.disableBefore === "string") {
      return new Date(this.question.disableBefore);
    }
    return this.question.disableBefore;
  }

  get disabled() {
    console.log(this.question.disableBefore);
    return this.question.isReadOnly ? true : { before: this.disableBefore };
  }

  get style(): CSSProperties {
    return this.question.getPropertyValue("readOnly") ||
      this.question.isDesignMode
      ? { pointerEvents: "none" }
      : {};
  }

  renderCalendar() {
    const { selectedDate, selectedRange } = this.state;
    const defaultClassNames = getDefaultClassNames();

    const commonClassNames: Partial<ClassNames> = {
      today: `${defaultClassNames.today} font-semibold`,
      day_button: `${defaultClassNames.day_button} text-base`,
      chevron: `${defaultClassNames.chevron} w-5 h-5`,
      month_caption: `${defaultClassNames.month_caption} !font-medium`,
      selected: `${defaultClassNames.selected} !font-semibold`,
    };


    switch (this.mode) {
      case "single":
        return (
          <DayPicker
            disabled={this.disabled}
            animate
            captionLayout="dropdown"
            autoFocus
            mode="single"
            selected={selectedDate}
            onSelect={this.handleDayPickerSelect}
            required={this.question.isRequired}
            numberOfMonths={this.numberOfMonths}
            endMonth={new Date(2100, 11)}
            classNames={{
              ...commonClassNames,
            }}
          />
        );
      case "range":
        return (
          <DayPicker
            disabled={this.disabled}
            animate
            captionLayout="dropdown"
            autoFocus
            mode="range"
            selected={selectedRange}
            onSelect={this.handleRangePickerSelect}
            required={this.question.isRequired}
            numberOfMonths={this.numberOfMonths}
            endMonth={new Date(2100, 11)}
            classNames={{
              ...commonClassNames,
              range_start: `${defaultClassNames.range_start}`,
              range_end: `${defaultClassNames.range_end}`,
              range_middle: `${defaultClassNames.range_middle}`,
              disabled: `${defaultClassNames.disabled} opacity-50 bg-gray-100`,
            }}
          />
        );
      default:
        return null;
    }
  }

  renderElement() {
    return (
      <div style={this.style}>
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              disabled={this.question.isReadOnly}
              className={cn(
                "flex items-center justify-between w-full py-2.5 px-[14px] outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 rounded-lg shadow-xs text-xs md:text-sm leading-[16px] md:leading-6 font-regular text-gray-500 hover:text-gray-600 border-gray-200 hover:border-gray-200 hover:text-gray-500 disabled:bg-gray-50"
              )}
            >
              <span>{this.state.formattedDate}</span>
              <Calendar />
            </button>
          </Popover.Trigger>
          <Popover.Content
            sideOffset={10}
            className="z-50 p-4 bg-white rounded-lg border border-gray-300 shadow-lg"
          >
            {this.renderCalendar()}
          </Popover.Content>
        </Popover.Root>
      </div>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  CALENDAR_QUESTION_TYPE,
  (props) => {
    return createElement(SurveyQuestionCalendar, props);
  }
);
